import { RestError } from '@azure/core-http';
import { useEffect, useMemo, useState } from 'react';
import {
  useMutationGetOpenAllHubStatus,
  useMutationOpenAllHub,
  useMutationOpenAllHubAsync,
  useQueryGetHub,
  useQueryGetHubDetails
} from '../../hubs.queries';
import {
  KnownHubDetailsKioskDtoControlledBy,
  KnownOpenAllDoorsAsyncStatusDtoState,
  KnownOpenAllResponseDtoStatus,
  OpenAllDoorsAsyncStatusDto,
  OpenAllResponseDto
} from '@groundfloor-platform/mgmt-service-g-sdk';
import { useForm } from 'react-hook-form';
import { ConfirmationNote } from '../../../../ui/components/pure/modal-content/confirmation-modal.component';

export interface OpenAllHubActionProps {
  hubID: string;
  onAbort(): void;
  onSuccess(): void;
}

interface OpenAllHubActionState {
  loading: boolean;
  error?: RestError;
  positionDescription?: string; // The description of the selected side on double-sided hubs
  includeKioskDoor?: boolean;
  allowRetry?: boolean;
}

export interface IntentToOpen extends OpenAllHubActionState {
  current: 'OPEN_REQUEST_INTENT_PENDING';
}

export interface OpenRequestFailed extends OpenAllHubActionState {
  current: 'OPEN_REQUEST_FAILED';
  openAllResponse?: OpenAllResponseDto;
}

export interface OpenRequestSuccess extends OpenAllHubActionState {
  current: 'OPEN_REQUEST_SUCCESS';
}

export type OpenAllHubActionStates = IntentToOpen | OpenRequestFailed | OpenRequestSuccess;

export interface OpenAllHubActionHandlers {
  onCancelIntent(): void;
  onSendRequest(doorIDs?: string[]): void;
  onIncludeKioskDoorInput(includeKioskDoor: boolean): void;
  onDone(): void;
}

const initialState: OpenAllHubActionStates = {
  current: 'OPEN_REQUEST_INTENT_PENDING',
  loading: false
};

// Allow up to 3 minutes for the open all to completely finish
// Poll every 5 seconds for a maximum of 40 attempts.
const OPEN_ALL_STATUS_POLL_INTERVAL_MS = 5000;
const OPEN_ALL_STATUS_POLL_MAX_ATTEMPTS = 40;

function delay(ms: number) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function buildOpenAllResponseFromAsyncStatus(
  status: OpenAllDoorsAsyncStatusDto,
  requestedDoorIDs: string[]
): OpenAllResponseDto {
  if (status.result) {
    return status.result;
  }

  const failedDoorIDs = status.failedDoorIDs ?? requestedDoorIDs;
  const failCount = status.failCount ?? failedDoorIDs.length;
  const defaultReason =
    status.state === KnownOpenAllDoorsAsyncStatusDtoState.Timeout
      ? 'The open all request timed out.'
      : 'The open all request failed.';
  const failReasons = status.failReasons?.length ? status.failReasons : [status.errorMessage ?? defaultReason];

  return {
    status: KnownOpenAllResponseDtoStatus.Failed,
    tryCount: requestedDoorIDs.length,
    failCount,
    failReasons,
    failedDoorIDs,
    doors: []
  };
}

export function useOpenAllHubAction({ hubID, onAbort, onSuccess }: OpenAllHubActionProps) {
  const [state, setState] = useState<OpenAllHubActionStates>(initialState);
  const openAllHubAction = useMutationOpenAllHub(hubID);
  const openAllHubActionAsync = useMutationOpenAllHubAsync(hubID);
  const openAllHubStatusAction = useMutationGetOpenAllHubStatus(hubID);
  const hub = useQueryGetHub(hubID);
  const hubDetails = useQueryGetHubDetails(hubID);
  const noteForm = useForm<ConfirmationNote>({
    defaultValues: {
      content: '',
      // Default to private notes. The Hub Action modal does not support public notes.
      visibility: 'private'
    }
  });
  const [canSubmit, setCanSubmit] = useState(false);

  useEffect(() => {
    const canSubmitNow = noteForm.getValues('content').length > 0 && noteForm.formState.isValid;
    if (canSubmit !== canSubmitNow) {
      setCanSubmit(canSubmitNow);
    }
  }, [canSubmit, noteForm]);

  // Everything needs to be loaded before we display the action modal.
  const isLoading = hub.isLoading || hubDetails.isLoading;
  const isError = hub.error || hubDetails.isError;

  // Prepare the options for selecting which side of double-sided lockers
  const sideOptions = useMemo(() => {
    if (hub.isLoading) {
      return [];
    }
    return (
      hub.data?.doorPositionMappings?.map((doorPositionMapping) => ({
        label: doorPositionMapping.description,
        value: doorPositionMapping.description
      })) ?? []
    );
  }, [hub.data, hub.isLoading]);

  const multipleSidesProps = useMemo(() => {
    if (sideOptions.length < 2) {
      return undefined;
    }
    return {
      sideOptions,
      onSelectSide(positionDescription: string) {
        setState((state) => ({ ...state, positionDescription }));
      }
    };
  }, [sideOptions]);

  const lockers = useMemo(() => {
    if (hubDetails.isLoading) {
      return [];
    }
    return hubDetails.data?.towers?.flatMap((tower) => tower.lockers) ?? [];
  }, [hubDetails.data, hubDetails.isLoading]);

  const allDoorIds = useMemo(() => {
    return lockers
      .filter((locker) => (state.includeKioskDoor === true ? true : !locker.isKiosk))
      .flatMap((locker) => locker.doors)
      .filter((door) =>
        state.positionDescription === undefined ? true : door.positionDescription === state.positionDescription
      )
      .map((door) => door.id);
  }, [lockers, state.includeKioskDoor, state.positionDescription]);

  const shouldUseAsyncOpenAll = useMemo(() => {
    // Only use the async open all method if the kiosk is v3
    return !!(
      hubDetails.data?.kiosk?.iotHubID &&
      hubDetails.data.kiosk.controlledBy !== KnownHubDetailsKioskDtoControlledBy.ExternalAc
    );
  }, [hubDetails.data]);

  async function pollAsyncOpenAllStatus(requestID: string) {
    for (let attempt = 0; attempt < OPEN_ALL_STATUS_POLL_MAX_ATTEMPTS; attempt++) {
      // The status of an open all request can be polled
      const status = await openAllHubStatusAction.mutateAsync(requestID);
      if (
        status.state === KnownOpenAllDoorsAsyncStatusDtoState.Succeeded ||
        status.state === KnownOpenAllDoorsAsyncStatusDtoState.Failed ||
        status.state === KnownOpenAllDoorsAsyncStatusDtoState.Timeout
      ) {
        return status;
      }
      await delay(OPEN_ALL_STATUS_POLL_INTERVAL_MS);
    }
    return undefined;
  }

  const handlers: OpenAllHubActionHandlers = {
    onCancelIntent() {
      onAbort();
    },
    onSendRequest(doorIDs?: string[]) {
      if (state.includeKioskDoor === undefined || (multipleSidesProps && state.positionDescription === undefined)) {
        return;
      }
      const requestDoorIDs = doorIDs ?? allDoorIds;
      const requestDto = {
        doorIds: requestDoorIDs,
        includeKioskDoor: state.includeKioskDoor,
        note: {
          content: noteForm.getValues('content'),
          private: noteForm.getValues('visibility') === 'private'
        }
      };

      setState((prevState) => ({ ...prevState, loading: true, error: undefined }));

      void (async () => {
        if (shouldUseAsyncOpenAll) {
          try {
            const accepted = await openAllHubActionAsync.mutateAsync(requestDto);
            const status = await pollAsyncOpenAllStatus(accepted.id);

            if (!status) {
              setState((prevState) => ({
                ...prevState,
                current: 'OPEN_REQUEST_FAILED',
                loading: false,
                allowRetry: false,
                openAllResponse: {
                  status: KnownOpenAllResponseDtoStatus.Failed,
                  tryCount: requestDoorIDs.length,
                  failCount: requestDoorIDs.length,
                  failReasons: ['The open all request timed out.'],
                  failedDoorIDs: requestDoorIDs,
                  doors: []
                }
              }));
              return;
            }

            if (status.state === KnownOpenAllDoorsAsyncStatusDtoState.Succeeded) {
              setState((prevState) => ({ ...prevState, current: 'OPEN_REQUEST_SUCCESS', loading: false }));
              return;
            }

            setState((prevState) => ({
              ...prevState,
              current: 'OPEN_REQUEST_FAILED',
              loading: false,
              allowRetry: false,
              openAllResponse: buildOpenAllResponseFromAsyncStatus(status, requestDoorIDs)
            }));
            return;
          } catch (error) {
            setState((prevState) => ({
              ...prevState,
              current: 'OPEN_REQUEST_FAILED',
              loading: false,
              allowRetry: false,
              error: error as RestError
            }));
            return;
          }
        }

        try {
          const response = await openAllHubAction.mutateAsync(requestDto);
          if (response.status === KnownOpenAllResponseDtoStatus.Success) {
            setState((prevState) => ({ ...prevState, current: 'OPEN_REQUEST_SUCCESS', loading: false }));
            return;
          }
          setState((prevState) => ({
            ...prevState,
            current: 'OPEN_REQUEST_FAILED',
            loading: false,
            allowRetry: true,
            openAllResponse: response
          }));
        } catch (error) {
          setState((prevState) => ({
            ...prevState,
            current: 'OPEN_REQUEST_FAILED',
            loading: false,
            allowRetry: true,
            error: error as RestError
          }));
        }
      })();
    },
    onIncludeKioskDoorInput(includeKioskDoor) {
      switch (state.current) {
        case 'OPEN_REQUEST_INTENT_PENDING':
          setState((state) => ({ ...state, includeKioskDoor }));
          break;
        default:
          return;
      }
    },
    onDone() {
      switch (state.current) {
        case 'OPEN_REQUEST_SUCCESS':
          onSuccess();
          break;
        default:
          return;
      }
    }
  };

  return {
    state,
    handlers,
    multipleSides: multipleSidesProps,
    isLoading,
    isError,
    noteForm
  } as const;
}
